import React, { useState, useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { CustomerContext } from '../../../contexts/CustomerContext'
import useQueryString from '../../../hooks/use-query-string'
import Typography from '../../Typography'
import classes from './Sittings.module.sass'
import Button from '../../Button'
import { isEmailValid } from '../../../utils/functions'
import TextInput from '../../TextInput'
import {
  Cart,
  OrderPackage,
  Package,
  StudentImage,
} from '../../../models'
import Spinner from '../../Spinner'
import { routes } from '../../../routes'
import PreviewImage from '../../PreviewImage/PreviewImage'
import { IMAGE_ORIENTATION_CACHE } from '../../../utils/variables'
import FavoritePoseItem from '../../favorite-pose-item';
import seniorClasses from '../favorite-pose-selection/favorite-pose-selection.module.sass'
import { School } from '../../../models/_models'
import useImageOrientations from '../../../hooks/useImageOrientations';
import useImageOrientation from '../../../hooks/useImageOrientation';
import usePackagePoses from '../../../hooks/usePackagePoses';
import {
  getAppropriateBg,
  getAppropriateForeground
} from '../_ElementaryMultiPose/MultiPosePackageBuilder/steps/FavoritePoseSelection';

function ImagePreview({ backgrounds, pose }) {
  const { orientation } = useImageOrientation(pose.watermarkUrl || pose.imageUrl);

  const correctlyOrientatedBg = backgrounds.find((bg) => {
    const cachedData = IMAGE_ORIENTATION_CACHE[bg.imageUrl];

    return orientation === cachedData?.orientation;
  });

  if (!correctlyOrientatedBg) return null;

  return (
    <PreviewImage
      style={{ width: orientation === 'LANDSCAPE' ? '78%' : '50%' }}
      isPreviewHidden={false}
      classes={classes.multiPoseImg}
      foregroundUrl={pose.watermarkUrl || pose.imageUrl}
      backgroundUrl={correctlyOrientatedBg.imageUrl}
    />
  );
}

const Sittings = () => {
  const {
    sid: studentId,
    school: schoolId,
    package: packageId,
    access_code: accessCode,
    email: emailParam,
    season,
    from,
    message_id: messageId,
  } = useQueryString()
  const history = useHistory()
  const { customer, setCustomer } = useContext(CustomerContext)

  const [currentStudent, setCurrentStudent] = useState()
  const [currentPackage, setCurrentPackage] = useState()
  const [currentSchool, setCurrentSchool] = useState()
  const [studentImage, setStudentImage] = useState()
  const [background, setBackground] = useState()
  const [backgrounds, setBackgrounds] = useState([])
  const [email, setEmail] = useState(emailParam || customer.email || '')
  const [noImages, setNoImages] = useState(false)
  const [isPreorder, setIsPreorder] = useState(false)

  const [isFetching, setIsFetching] = useState(true)

  const { data: packagePoses, isLoading } = usePackagePoses(studentImage?.packageId || packageId);

  useEffect(() => {
    if (!currentStudent) return;
    if (!studentImage) return;

    localStorage.setItem(`currentSeason-${currentStudent.id}`, studentImage.folder);
  }, [currentStudent && currentStudent.id, studentImage && studentImage.folder]);

  useEffect(() => {
    if (isFetching) return;
    if (!emailParam && !currentStudent?.email) return;
    if (!studentImage) return;
    if (!currentStudent) return;
    if (!currentSchool) return;
    if (!currentPackage) return;

    const cart = customer.cart ? (
      customer.cart
    ) : (
      new Cart({ email, schoolId })
    )

    cart.createdLocation = from || 'qr_code' || 'sittings';
    cart.messageId = messageId;

    if (cart.save) {
      cart.save().then(() => {
        setCustomer({ ...customer, currentStudent, email, currentSchool, cart });
        if (packageId) {
          history.push(routes.elementaryMultiPoseBuilder(currentPackage.id))
        } else {
          history.push(routes.packageSelection({ pid: currentPackage.id }));
        }
      })
    }
  }, [emailParam, studentImage, currentStudent, currentSchool, isFetching, isPreorder, currentPackage]);

  useEffect(() => {
    if (!currentStudent?.email) return;
    if (email) return;

    setEmail(currentStudent.email);
    setCustomer({ ...customer, email: currentStudent.email });
  }, [currentStudent?.email]);

  useImageOrientations(backgrounds.map((bg) => bg.imageUrl));

  localStorage.setItem('isUsingAccessCode', true);

  useEffect(() => {
    School
      .find(schoolId)
      .then((res) => {
        setCurrentSchool(res.data)
      })
  }, [schoolId])

  useEffect(() => {
    // const enrolledParam = season === 'fall' ? {} : { is_enrolled: true };

    async function getData() {
      let studentImageRes
      let isMultipleSeasonPackage;
      let parsedAccessCode;

      if (accessCode.toUpperCase().includes('S-')) {
        parsedAccessCode = accessCode.toUpperCase().split('S-')[1];
      }
      else if (accessCode.toUpperCase().includes('P-')) {
        parsedAccessCode = accessCode.toUpperCase().split('P-')[1];
      }
      else if (accessCode.toUpperCase().includes('M-')) {
        parsedAccessCode = accessCode.toUpperCase().split('M-')[1];
      } else {
        parsedAccessCode = accessCode;
      }

      if (season === 'seniors') {
        studentImageRes = await StudentImage
          .selectExtra(['packageId'])
          .includes(['senior_images', 'student'])
          .where({ for_access_code_and_school: [parsedAccessCode, schoolId], isSenior: true, hasPoses: true })
          .first()
      } else {
        studentImageRes = await StudentImage
          .selectExtra(['packageId'])
          .includes(['poses', 'student'])
          .where({ for_access_code_and_school: [parsedAccessCode, schoolId] })
          .first()
      }

      const { data } = studentImageRes

      if (!data) {
        setNoImages(true)
        return
      }

      if (!packageId && !data.packageId) {
        setNoImages(true);

        return;
      }

      const schoolPackageRes = await Package
        .includes({ backgroundSet: ['backgrounds', 'defaultBackground'] })
        .find(packageId || data.packageId)

      const { data: schoolPackage } = schoolPackageRes;

      if (!data && schoolPackage.season !== 'fall2023') {
        setNoImages(true)
        return
      }

      const student = data.student;

      if (schoolPackage.multiple && (data && data.poses && data.poses.length === 0 && season !== 'seniors')) {
        setNoImages(true)
        return
      }

      setCurrentStudent(student)
      setBackgrounds(schoolPackage?.backgroundSet?.backgrounds || []);
      setBackground(schoolPackage.backgroundSet && schoolPackage.backgroundSet.defaultBackground)
      setStudentImage(data)

      setCurrentPackage(schoolPackage);
      setIsFetching(false)
    }

    getData()
  }, [studentId, schoolId, season])

  const submitHandler = async (e) => {
    e.preventDefault()

    const cart = customer.cart ? (
      customer.cart
    ) : (
      new Cart({ email, schoolId })
    )

    cart.createdLocation = from || 'qr_code' || 'sittings';
    cart.messageId = messageId;

    if (cart.save) await cart.save()

    const currentOrderPackage = new OrderPackage({
      packageId: currentPackage.id,
      cartId: cart.id,
    })

    setCustomer({
      ...customer,
      email,
      cart,
      currentOrderPackage,
      currentStudent,
      currentSchool,
    })

    if (season === 'seniors') {
      history.push(routes.seniors.packageBuilder(currentPackage.id));
    } else if (packageId) {
      history.push(routes.elementaryMultiPoseBuilder(currentPackage.id))
    } else if (currentPackage.multiple) {
      history.push(routes.packageSelection({ pid: currentPackage.id }));
      // history.push(routes.elementaryMultiPoseBuilder(currentPackage.id, { preorder: isPreorder }))
    } else {
      history.push(routes.backgroundSelection(currentPackage.id))
    }
  }

  const studentFullName = ({ firstName, lastName }) => `${firstName}${lastName ? ` ${lastName}` : ''}`

  const renderPreview = () => {
    if (studentImage && studentImage.poses && studentImage.poses.length > 1) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {studentImage.poses.map((pose) => (
            <ImagePreview pose={pose} backgrounds={backgrounds} />
          ))}
        </div>
      )
    }

    if (season !== 'seniors' && !studentImage.watermarkUrl.includes('missing')) return (
      <PreviewImage
        isPreviewHidden={false}
        classes={classes.studentImage}
        foregroundUrl={studentImage.watermarkUrl || studentImage.imageUrl}
        backgroundUrl={background.imageUrl}
      />
    )

    return (
      <div className={seniorClasses.outer}>
        <div className={`${seniorClasses.container} ${seniorClasses.sittingsContainer}`}>
          {studentImage.seniorImages.map((seniorImage) => {
            const priorityBg = getAppropriateBg(packagePoses, seniorImage, backgrounds);
            const foreground = getAppropriateForeground(packagePoses, seniorImage);

            return (
              <FavoritePoseItem
                priorityBg={priorityBg}
                isSittings
                key={seniorImage.id}
                id={seniorImage.id}
                fileName={seniorImage.imageFileName}
                foreground={seniorImage.watermarkUrl}
                background={background.imageUrl}
                backgrounds={backgrounds}
                alt="senior image"
                actualForeground={foreground?.imageUrl}
                onImageClick={() => {
                }}
                onFavoriteClick={() => {
                }}
              />
            );
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <Typography
        tag="h1"
        weight="bold"
        align="center"
        classes={classes.pageTitle}
      >
        Shoob Photography Sittings
      </Typography>
      {!studentImage && !noImages && isLoading && (
        <>
          <Typography align="center" classes={classes.instructions}>
            Looking for your student&apos;s images...
          </Typography>
          <div className={classes.spinnerContainer}>
            <Spinner color="blue" />
          </div>
        </>
      )}
      {(studentImage || isPreorder) && (
        <div>
          {studentImage && renderPreview()}
          <Typography
            align="center"
            weight="bold"
            classes={classes.studentName}
          >
            {studentFullName(currentStudent)}
          </Typography>
          <div>
            <Typography align="center" classes={classes.instructions}>
              Please enter your email to purchase your {packageId ? 'package' : 'pictures'}
            </Typography>
            <form className={classes.form} onSubmit={submitHandler}>
              <TextInput
                placeholder="Email Address"
                type="email"
                val={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                rounded
                fullWidth
                variant="submit"
                disabled={!isEmailValid(email)}
              >
                Continue
              </Button>
            </form>
          </div>
        </div>
      )}
      {noImages && !isPreorder && (
        <Typography
          align="center"
          classes={classes.instructions}
        >
          Your student&apos;s images are not available at the moment.
          Please check back in 1-3 business days.
        </Typography>
      )}
    </div>
  )
}

export default Sittings
